import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Trash2, Scissors } from "lucide-react";
import { BridgeApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { PageHeader } from "../components/PageScaffold";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { relativeTime, shortTime, formatBytes } from "../lib/format";
import { cn } from "../lib/cn";
import type { JournalEntry, JournalMeta } from "../lib/types";

export default function Journals() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: journals = [], isLoading, error } = useQuery({
    queryKey: qk.bridge.journals,
    queryFn: BridgeApi.journals,
  });

  const deleteMut = useMutation({
    mutationFn: BridgeApi.deleteJournal,
    onSuccess: () => {
      toast.success("Journal deleted");
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: qk.bridge.journals });
    },
  });
  const truncateMut = useMutation({
    mutationFn: ({ id, keep }: { id: string; keep: number }) => BridgeApi.truncateJournal(id, keep),
    onSuccess: () => {
      toast.success("Truncated");
      qc.invalidateQueries({ queryKey: qk.bridge.journals });
      if (selectedId) qc.invalidateQueries({ queryKey: qk.bridge.journal(selectedId) });
    },
  });

  const sorted = [...journals].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  const totalSize = journals.reduce((s, j) => s + j.size, 0);
  const totalEntries = journals.reduce((s, j) => s + j.entries, 0);

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Journals"
        icon={BookOpen}
        subtitle={`${journals.length} journals · ${totalEntries.toLocaleString()} entries · ${formatBytes(totalSize)}`}
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<JournalMeta>
          items={sorted}
          itemKey={(j) => j.id}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={isLoading}
          error={error as Error | null}
          itemHeight={66}
          emptyState={<EmptyState icon={<BookOpen className="size-8" />} title="No journals" />}
          renderItem={({ item }) => <JournalRow j={item} />}
          renderDetail={(j) => (
            <JournalDetail
              meta={j}
              onTruncate={(n) => truncateMut.mutate({ id: j.id, keep: n })}
              onDelete={() => {
                if (confirm(`Delete journal "${j.name}"?`)) deleteMut.mutate(j.id);
              }}
            />
          )}
        />
      </div>
    </div>
  );
}

function JournalRow({ j }: { j: JournalMeta }) {
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <Pill tone={j.type === "dm" ? "accent" : "info"}>{j.type}</Pill>
        <span className="text-sm font-medium text-fg truncate">{j.name}</span>
        <span className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap">
          {relativeTime(j.updated_at).replace(" ago", "")}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs text-fg-muted">
        <span>{j.entries.toLocaleString()} entries</span>
        <span className="text-fg-subtle">·</span>
        <span>{formatBytes(j.size)}</span>
      </div>
    </div>
  );
}

function JournalDetail({
  meta,
  onTruncate,
  onDelete,
}: {
  meta: JournalMeta;
  onTruncate: (n: number) => void;
  onDelete: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: qk.bridge.journal(meta.id),
    queryFn: () => BridgeApi.journal(meta.id),
  });

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Pill tone={meta.type === "dm" ? "accent" : "info"}>{meta.type}</Pill>
            <h2 className="text-base font-semibold text-fg truncate">{meta.name}</h2>
          </div>
          <p className="text-xs text-fg-muted mt-1">
            {meta.entries.toLocaleString()} entries · {formatBytes(meta.size)} · updated {relativeTime(meta.updated_at)}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              const v = prompt("Keep last N entries:", "50");
              if (v !== null) {
                const n = parseInt(v, 10);
                if (Number.isFinite(n) && n > 0) onTruncate(n);
              }
            }}
          >
            <Scissors className="size-3.5" />
            Truncate
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Delete">
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2 min-h-0">
        {isLoading && <CenteredSpinner />}
        {data && data.entries.length === 0 && (
          <div className="text-sm text-fg-muted text-center py-8">Empty journal.</div>
        )}
        {data && data.entries.map((e, i) => <JournalEntryRow key={i} entry={e as JournalEntry} />)}
      </div>
    </div>
  );
}

function JournalEntryRow({ entry }: { entry: JournalEntry }) {
  const isMe = entry.from === "me" || entry.from === "self";
  return (
    <div className={cn("rounded-md px-3 py-2 text-sm border border-border", isMe ? "bg-accent-subtle/30" : "bg-surface-2")}>
      <div className="flex items-center gap-2 text-[11px] text-fg-muted mb-1">
        <Pill>{entry.channel}</Pill>
        <span className="font-medium text-fg">{entry.from}</span>
        <span className="ml-auto font-mono">{shortTime(entry.ts)}</span>
      </div>
      <p className="text-fg whitespace-pre-wrap [overflow-wrap:anywhere]">{entry.body}</p>
      {entry.attachments && entry.attachments.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {entry.attachments.map((a, i) => (
            <Pill key={i} tone="info">
              📎 {a.filename ?? a.type}
              {a.size ? ` · ${formatBytes(a.size)}` : ""}
            </Pill>
          ))}
        </div>
      )}
      {entry.reply_to && (
        <div className="mt-1 text-[10px] text-fg-subtle italic">
          ↪ replying to {entry.reply_to.display}
        </div>
      )}
    </div>
  );
}
